import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { setupInterceptors } from '@/api/clients/axios';
import { getNavigationSchema } from '@/routes/navigation';
import { useToast } from '@/context/ToastContext';
import { getGatewayApiAuthAccount } from '@/auth/GatewayApiSilentSso';
import { MyPlayerService, OrderSettingsService } from '@/service';
import { getUserWithMyPlayers } from '@/api/endpoints';
import { UserSessionHelper, WebViewHelper, UserPermissionHelper, UserSettingsHelper } from '@/library';
import { isIOSWebView } from '@/utils';
import Spinner from '@/common/Spinner/Spinner';
import styles from './SessionGuard.module.scss';
import { setupGatewayApiAuth } from '@/api/clients/gatewayAxios';

export default function SessionGuard() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [authenticating, setAuthenticating] = useState(true);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const isPlayerRoute = getNavigationSchema(location.pathname)?.group === 'player';

  useEffect(() => {
    const asyncProcess = async () => {
      const userSession = await UserSessionHelper.getUser();

      if (!userSession || UserSessionHelper.isSessionExpired(userSession)) {
        // clear user session and proceed with logout
        await UserSessionHelper.clearSession();
        if (isIOSWebView) {
          WebViewHelper.iOSLogout({ expiredSession: true });
        } else {
          if (userSession) {
            // if the session exists its expired so we signout
            await UserSessionHelper.signOut(instance);
          }
          navigate('/', { replace: true }); // use replace to avoid back navigation to protected routes
        }
        return; // stop flow
      }

      setAuthenticating(false); // authentication flow is done
      setupInterceptors(userSession.token); // if session is valid we set the token in the axios client

      try {
        setIsInitializingSession(true); // initializing session data

        if (!isIOSWebView) {
          try {
            const gatewayAuthAccount = await getGatewayApiAuthAccount(instance, userSession.email);
            setupGatewayApiAuth(instance, gatewayAuthAccount);
          } catch (error) {
            console.error('Failed to initialize Gateway API auth account:', error);
            showToast({ message: 'Please sign in again to continue using the photos feature.', status: 'error' });
          }
        }

        const appUser = await getUserWithMyPlayers();

        await Promise.all([
          await UserPermissionHelper.setUserPermissions(appUser), // fetch the user permissions
          await UserSettingsHelper.saveUserSettings(appUser), // fetch the user specific settings (subnav order)
          await OrderSettingsService.cacheUserSettings(appUser), // persist order settings in the user session
          await MyPlayerService.saveMyPlayers(appUser), // save the users favorite players to IndexedDB
        ]);
      } finally {
        setIsInitializingSession(false);
      }
    };
    asyncProcess();
  }, [instance, navigate, showToast]);

  if (authenticating) return null;
  if (isInitializingSession)
    return (
      <div className={`${styles.loadingOverlay} ${isPlayerRoute ? styles.playerOffset : ''}`}>
        <div className={styles.loadingContainer}>
          <Spinner />
          <div className={styles.loadingLabel}>Loading your session…</div>
        </div>
      </div>
    );

  return (
    <>
      {/* renders matched child route (e.g., <TourBag />) */}
      <Outlet />
    </>
  );
}
